import React, { useState, useEffect, useRef } from 'react';
import { 
    Clock, CheckCircle, AlertCircle, AlertTriangle, QrCode, 
    Camera, User, Dumbbell, ShieldCheck, Users, RotateCw, XCircle 
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../services/api';
import ReabonnementModal from '../components/ReabonnementModal';
import ReceiptModal from '../components/ReceiptModal';

// Nettoie le code reçu de la douchette. Si Windows est en AZERTY alors que la douchette envoie des
// touches QWERTY, "TKD-2026-0001" arrive sous la forme "TKD)éàé-)ààà&" : on reconstitue le code d'origine.
const AZERTY_TO_INTENDED: Record<string, string> = {
    '&': '1', 'é': '2', '"': '3', "'": '4', '(': '5', '-': '6', 'è': '7', '_': '8', 'ç': '9', 'à': '0', ')': '-',
    'q': 'a', 'Q': 'A', 'a': 'q', 'A': 'Q', 'w': 'z', 'W': 'Z', 'z': 'w', 'Z': 'W', ',': 'm', '?': 'M'
};

const normalizeCode = (raw: string): string => {
    let code = raw.replace(/[\u0000-\u001F\u007F\s]/g, '').trim();
    if (/[&é"'(èçà_)]/.test(code)) {
        code = code.split('').map(c => AZERTY_TO_INTENDED[c] ?? c).join('');
    }
    return code.replace(/^([a-z]+)-/i, (_m, p) => p.toUpperCase() + '-');
};

const Pointage = () => {
    const [attendances, setAttendances] = useState<any[]>([]);
    const [clientId, setClientId] = useState('');
    const [loading, setLoading] = useState(false);
    const [scanResult, setScanResult] = useState<any>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [isReaboOpen, setIsReaboOpen] = useState(false);
    const [reaboClient, setReaboClient] = useState<any>(null);
    const [receiptData, setReceiptData] = useState<any>(null);
    const [isReceiptOpen, setIsReceiptOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const scannerRef = useRef<Html5Qrcode | null>(null);

    const fetchAttendances = async () => {
        try {
            const res = await api.get('/attendances');
            setAttendances(res.data);
        } catch (error) {
            console.error("Erreur lors de la récupération des présences", error);
        }
    };

    useEffect(() => {
        fetchAttendances();
        if (inputRef.current) {
            inputRef.current.focus();
        }
        
        return () => {
            if (scannerRef.current && scannerRef.current.isScanning) {
                scannerRef.current.stop().catch(console.error);
            }
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        let qrInstance: Html5Qrcode | null = null;

        if (showCamera) {
            setCameraError(null);

            // Temporisation brève pour s'assurer que le container DOM est présent
            const timer = setTimeout(async () => {
                if (!isMounted) return;
                const container = document.getElementById("qr-reader");
                if (!container) return;

                try {
                    qrInstance = new Html5Qrcode("qr-reader");
                    scannerRef.current = qrInstance;

                    const cameras = await Html5Qrcode.getCameras();
                    if (!isMounted) return;

                    if (!cameras || cameras.length === 0) {
                        setCameraError("Aucune caméra détectée sur votre ordinateur.");
                        return;
                    }

                    const cameraId = cameras[0].id;
                    await qrInstance.start(
                        cameraId,
                        { fps: 10, qrbox: { width: 250, height: 250 } },
                        (decodedText) => {
                            setClientId(decodedText);
                            setShowCamera(false);
                            processCheckIn(decodedText);
                        },
                        () => {}
                    );
                } catch (err: any) {
                    console.error("Erreur caméra Pointage:", err);
                    if (isMounted) {
                        const errMsg = String(err?.message || err || '');
                        if (errMsg.includes('NotAllowedError') || errMsg.includes('Permission') || err?.name === 'NotAllowedError') {
                            setCameraError("L'accès à la caméra est bloqué dans votre navigateur. Cliquez sur l'icône de cadenas ou de caméra à gauche de https://hopfitness.ci dans la barre d'adresse pour passer sur Autoriser.");
                        } else if (!errMsg.includes('already under transition')) {
                            setCameraError("Impossible d'activer la caméra : " + errMsg);
                        }
                    }
                }
            }, 100);

            return () => {
                isMounted = false;
                clearTimeout(timer);
                if (qrInstance) {
                    try {
                        if (qrInstance.isScanning) {
                            qrInstance.stop().then(() => qrInstance?.clear()).catch(() => {});
                        } else {
                            qrInstance.clear();
                        }
                    } catch (e) {}
                    scannerRef.current = null;
                }
            };
        } else {
            if (scannerRef.current) {
                try {
                    if (scannerRef.current.isScanning) {
                        scannerRef.current.stop().then(() => scannerRef.current?.clear()).catch(() => {});
                    } else {
                        scannerRef.current.clear();
                    }
                } catch (e) {}
                scannerRef.current = null;
            }
        }
    }, [showCamera]);

    // Douchette (mode clavier) : capture les frappes rapides même si le champ a perdu le focus
    useEffect(() => {
        let buffer = '';
        let lastKey = 0;
        const onKeyDown = (e: KeyboardEvent) => {
            if (showCamera) return;
            const target = e.target as HTMLElement;
            if (target === inputRef.current) return; // le champ gère déjà la saisie
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
            const now = Date.now();
            if (now - lastKey > 100) buffer = '';
            lastKey = now;
            if (e.key === 'Enter') {
                const code = buffer.trim();
                buffer = '';
                if (code.length >= 1) processCheckIn(code);
            } else if (e.key.length === 1) {
                buffer += e.key;
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [showCamera]);

    const processCheckIn = async (idToProcess: string) => {
        idToProcess = normalizeCode(idToProcess);
        console.log('Code scanné :', JSON.stringify(idToProcess));
        setLoading(true);
        setScanResult(null);
        
        try {
            const res = await api.post('/attendances/checkin', { client_id: idToProcess });
            setScanResult({ success: true, data: res.data });
            setClientId('');
            // Seuls les passages validés et enregistrés rafraîchissent la liste
            if (res.data.attendanceRecorded) {
                fetchAttendances();
            }
        } catch (error: any) {
            setScanResult({ success: false, error: error.response?.data?.error || 'Erreur lors du check-in' });
            setClientId('');
        } finally {
            setLoading(false);
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    };

    const handleCheckIn = (e: React.FormEvent) => {
        e.preventDefault();
        const code = clientId.trim();
        if (code) processCheckIn(code);
    };

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-black text-brand-dark flex items-center gap-3">
                <Clock className="text-brand-primary" size={32} />
                Pointage Rapide
            </h1>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 max-w-2xl">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-gray-700">
                        <QrCode size={24} />
                        <h2 className="text-xl font-bold">Scanner la carte / Entrer l'ID</h2>
                    </div>
                    <button 
                        type="button"
                        onClick={() => setShowCamera(!showCamera)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md font-bold transition-colors ${showCamera ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                        <Camera size={18} />
                        {showCamera ? 'Fermer la caméra' : 'Utiliser la caméra'}
                    </button>
                </div>

                {showCamera && (
                    <div className="mb-6 overflow-hidden rounded-xl border-2 border-brand-primary bg-black/5 p-2">
                        {cameraError ? (
                            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex flex-col gap-2">
                                <div className="font-bold flex items-center gap-2">
                                    <AlertCircle size={18} />
                                    Accès Caméra Bloqué
                                </div>
                                <p>{cameraError}</p>
                                <div className="text-xs bg-white p-2 rounded border border-red-100 text-gray-700">
                                    👉 Pour autoriser : Cliquez sur l'icône de cadenas/caméra située tout à gauche de l'adresse <strong>https://hopfitness.ci</strong> dans votre navigateur, puis passez la Caméra sur <strong>Autoriser</strong> et rafraîchissez la page.
                                </div>
                            </div>
                        ) : (
                            <div id="qr-reader" className="w-full rounded-lg overflow-hidden min-h-[260px]"></div>
                        )}
                    </div>
                )}
                
                <form onSubmit={handleCheckIn} className="flex gap-4 items-end mb-6">
                    <div className="flex-1">
                        <input
                            ref={inputRef}
                            type="text"
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            placeholder="Scannez avec une douchette ou tapez l'ID..."
                            className="w-full px-4 py-3 border-2 border-brand-primary rounded-md focus:outline-none focus:ring-4 focus:ring-yellow-200 text-lg font-medium"
                            required
                            onBlur={() => { if (!showCamera) setTimeout(() => inputRef.current?.focus(), 100); }}
                            autoComplete="off"
                            autoFocus
                            disabled={showCamera}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading || showCamera}
                        className="px-6 py-3 bg-brand-primary text-brand-dark font-bold rounded-md hover:bg-yellow-500 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                        <CheckCircle size={20} />
                        Pointer
                    </button>
                </form>

                {/* Affichage du résultat du scan */}
                {scanResult && (
                    <div className={`p-6 rounded-xl border-2 transition-all ${
                        scanResult.success 
                            ? (scanResult.data.isTaekwondo 
                                ? 'bg-yellow-50/70 border-yellow-400' 
                                : (scanResult.data.isCoach 
                                    ? 'bg-blue-50 border-blue-200' 
                                    : (scanResult.data.subscription?.isExpired 
                                        ? 'bg-red-50 border-red-300' 
                                        : 'bg-green-50 border-green-300'))) 
                            : 'bg-red-50 border-red-200'
                    }`}>
                        {!scanResult.success ? (
                            <div className="flex items-center gap-3 text-red-700">
                                <AlertCircle size={28} />
                                <span className="text-lg font-bold">{scanResult.error}</span>
                            </div>
                        ) : scanResult.data.isTaekwondo ? (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between border-b border-yellow-200 pb-3">
                                    <span className="text-xs font-black uppercase tracking-wider text-yellow-800 flex items-center gap-2">
                                        🥋 Contrôle Sécurité Taekwondo Enfants
                                    </span>
                                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-black text-yellow-400">
                                        {scanResult.data.scanType === 'student' ? 'BADGE ÉLÈVE ENTRÉE' : 'SORTIE AUTORISÉE'}
                                    </span>
                                </div>

                                {scanResult.data.scanType === 'student' ? (
                                    <div>
                                        <div className="flex items-start gap-4 mb-4">
                                            {scanResult.data.student.photo_url ? (
                                                <img src={scanResult.data.student.photo_url} alt="enfant" className="w-20 h-24 rounded-lg object-cover border-2 border-yellow-500 shadow-sm" />
                                            ) : (
                                                <div className="w-20 h-24 rounded-lg bg-yellow-100 border border-yellow-400 flex items-center justify-center text-xs font-bold text-yellow-800">ÉLÈVE</div>
                                            )}
                                            <div>
                                                <h3 className="text-2xl font-black text-gray-900">
                                                    {scanResult.data.student.first_name} {scanResult.data.student.last_name}
                                                </h3>
                                                <p className="text-xs text-gray-600 mt-1">
                                                    Carte : <strong>{scanResult.data.student.card_number}</strong> | Niveau : <strong className="text-yellow-700">{scanResult.data.student.level}</strong>
                                                </p>
                                                <p className="text-xs text-gray-600">
                                                    Parent : <strong>{scanResult.data.student.parent_name}</strong> ({scanResult.data.student.parent_phone})
                                                </p>
                                                <p className="text-xs font-extrabold text-green-700 mt-2">
                                                    ✓ Check-in cours de Taekwondo enregistré avec succès
                                                </p>
                                            </div>
                                        </div>

                                        {scanResult.data.student.pickups && scanResult.data.student.pickups.length > 0 && (
                                            <div className="bg-white p-3 rounded-xl border border-yellow-200">
                                                <h4 className="text-xs font-bold text-gray-700 uppercase mb-2">
                                                    Personnes autorisées pour la sortie ({scanResult.data.student.pickups.length}) :
                                                </h4>
                                                <div className="space-y-1.5">
                                                    {scanResult.data.student.pickups.map((p: any, i: number) => (
                                                        <div key={i} className="text-xs flex items-center justify-between p-1.5 rounded bg-gray-50 border">
                                                            <span><strong>{p.first_name} {p.last_name}</strong> ({p.relationship})</span>
                                                            <span className="font-mono text-[11px] text-gray-500">{p.phone}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-start gap-4">
                                        {scanResult.data.pickup.photo_url ? (
                                            <img src={scanResult.data.pickup.photo_url} alt="tuteur" className="w-20 h-24 rounded-lg object-cover border-2 border-green-500 shadow-sm" />
                                        ) : (
                                            <div className="w-20 h-24 rounded-lg bg-green-100 border border-green-400 flex items-center justify-center text-xs font-bold text-green-800">TUTEUR</div>
                                        )}
                                        <div>
                                            <h3 className="text-2xl font-black text-gray-900">
                                                {scanResult.data.pickup.first_name} {scanResult.data.pickup.last_name}
                                            </h3>
                                            <p className="text-xs text-gray-700 mt-1">
                                                Carte Tuteur : <strong>{scanResult.data.pickup.card_number}</strong> ({scanResult.data.pickup.relationship})
                                            </p>
                                            <div className="mt-2 p-2.5 rounded-lg bg-green-100 border border-green-300 text-xs font-bold text-green-800">
                                                ✓ AUTORISÉ(E) À RÉCUPÉRER L'ENFANT : {scanResult.data.pickup.student_first_name} {scanResult.data.pickup.student_last_name} ({scanResult.data.pickup.student_card_number})
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-start gap-4">
                                {scanResult.data.client.photo_url ? (
                                    <img src={scanResult.data.client.photo_url} alt="client" className="w-20 h-20 rounded-lg object-cover border-2 border-white shadow-sm" />
                                ) : (
                                    <div className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-medium">Pas de photo</div>
                                )}
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-2xl font-black text-gray-800">
                                            {scanResult.data.client.first_name} {scanResult.data.client.last_name}
                                        </h3>
                                        {scanResult.data.subscription?.isExpired ? (
                                            <span className="px-3 py-1 bg-red-600 text-white font-extrabold text-xs rounded-full uppercase tracking-wider">
                                                ACCÈS REFUSÉ
                                            </span>
                                        ) : (
                                            <span className="px-3 py-1 bg-green-600 text-white font-extrabold text-xs rounded-full uppercase tracking-wider">
                                                ACCÈS AUTORISÉ
                                            </span>
                                        )}
                                    </div>
                                    
                                    <div className="mt-3 space-y-2">
                                        {scanResult.data.isCoach ? (
                                            <div className="flex items-center gap-2">
                                                <span className="px-3 py-1 bg-blue-100 text-blue-700 font-bold rounded-full text-sm">STAFF / COACH</span>
                                                <span className="text-blue-800 font-bold">Présence coach enregistrée</span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-600 font-medium w-32">Forfait / Accès :</span>
                                                    {scanResult.data.subscription.isExpired ? (
                                                        <span className="px-3 py-1 bg-red-100 text-red-700 font-bold rounded-full text-sm">
                                                            {scanResult.data.subscription.status} ({scanResult.data.subscription.type_name})
                                                        </span>
                                                    ) : (
                                                        <span className="px-3 py-1 bg-green-100 text-green-700 font-bold rounded-full text-sm">
                                                            {scanResult.data.subscription.status} ({scanResult.data.subscription.type_name})
                                                        </span>
                                                    )}
                                                </div>
                                                {scanResult.data.subscription.end_date && (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-gray-600 font-medium w-32">
                                                            {scanResult.data.isSeance ? 'Date séance :' : 'Valide jusqu\'au :'}
                                                        </span>
                                                        <span className="text-gray-800 font-bold">
                                                            {new Date(scanResult.data.subscription.end_date).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                )}
                                                {scanResult.data.subscription.isExpired ? (
                                                    <div className="mt-4 p-4 bg-red-100/90 border-2 border-red-300 rounded-xl space-y-3">
                                                        <div className="flex items-start gap-3">
                                                            <div className="p-2 bg-red-200 text-red-700 rounded-lg shrink-0">
                                                                <AlertTriangle size={24} />
                                                            </div>
                                                            <div className="flex-1">
                                                                <h4 className="font-black text-sm uppercase tracking-wide text-red-900">
                                                                    Abonnement Expiré — Réabonnement Requis
                                                                </h4>
                                                                <p className="text-xs text-red-800 mt-1 leading-relaxed">
                                                                    L'abonnement de ce client a expiré{scanResult.data.subscription.end_date ? ` le ${new Date(scanResult.data.subscription.end_date).toLocaleDateString('fr-FR')}` : ''}.
                                                                </p>
                                                                <p className="text-xs font-black text-red-700 mt-1 flex items-center gap-1.5">
                                                                    <XCircle size={15} className="shrink-0" />
                                                                    <span>Le passage n'a PAS été enregistré dans les derniers passages.</span>
                                                                </p>
                                                                <p className="text-xs text-gray-700 mt-1">
                                                                    Veuillez procéder au réabonnement pour autoriser l'accès à la salle.
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="pt-2 border-t border-red-200 flex items-center justify-between">
                                                            <span className="text-[11px] font-bold text-red-600">
                                                                Accès refusé au tourniquet / salle
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setReaboClient(scanResult.data.client);
                                                                    setIsReaboOpen(true);
                                                                }}
                                                                className="px-4 py-2 bg-brand-primary text-brand-dark hover:bg-yellow-400 font-extrabold text-xs rounded-lg transition-all shadow-sm flex items-center gap-1.5"
                                                            >
                                                                <RotateCw size={14} />
                                                                Réabonner ce client
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-xs font-bold text-green-700 mt-2 flex items-center gap-1.5">
                                                        <CheckCircle size={15} /> Présence enregistrée automatiquement dans les passages.
                                                    </p>
                                                )}
                                                {scanResult.data.subscription.groupMembers && scanResult.data.subscription.groupMembers.length > 0 && (
                                                    <div className="mt-3 p-3.5 bg-purple-50/90 border border-purple-200 rounded-xl text-left">
                                                        <div className="text-xs font-black uppercase text-purple-900 flex items-center gap-1.5 mb-2">
                                                            <Users size={15} className="text-purple-600" />
                                                            <span>Membres autorisés pour ce Pack Entreprise ({scanResult.data.subscription.groupMembers.length}/5 max) :</span>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                                            {scanResult.data.subscription.groupMembers.map((m: any, idx: number) => (
                                                                <div key={idx} className="flex items-center justify-between text-xs bg-white px-2.5 py-1.5 rounded-lg border border-purple-100 shadow-2xs">
                                                                    <span className="font-bold text-gray-800">{idx + 1}. {m.full_name || m.name}</span>
                                                                    {m.phone && <span className="font-mono text-gray-500 text-[11px]">{m.phone}</span>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mt-6">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h2 className="text-lg font-bold text-gray-800">Derniers passages</h2>
                    <span className="text-xs text-gray-500 font-medium">Clients, Séances, Enfants Taekwondo & Coachs</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-100 border-b border-gray-200 text-gray-600 text-sm">
                                <th className="p-4 font-medium">Date et Heure</th>
                                <th className="p-4 font-medium">Membre / Participant</th>
                                <th className="p-4 font-medium">Catégorie</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attendances.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="p-4 text-center text-gray-500">Aucun pointage récent.</td>
                                </tr>
                            ) : (
                                attendances.map((att: any) => (
                                    <tr key={`${att.type}-${att.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="p-4 text-sm font-medium text-gray-600 whitespace-nowrap">
                                            {new Date(att.check_in_time).toLocaleString('fr-FR', {
                                                day: '2-digit', month: '2-digit', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit'
                                            })}
                                        </td>
                                        <td className="p-4 font-medium text-gray-800 flex items-center gap-3">
                                            {att.photo_url ? (
                                                <img src={att.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                                            ) : (
                                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                                                    {att.type === 'coach' ? <Dumbbell size={16} /> : att.type === 'taekwondo' ? <ShieldCheck size={16} /> : <User size={16} />}
                                                </div>
                                            )}
                                            <span>{att.first_name} {att.last_name}</span>
                                        </td>
                                        <td className="p-4">
                                            {att.type === 'coach' ? (
                                                <span className="text-xs font-bold px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full">ENTRAÎNEUR</span>
                                            ) : att.type === 'taekwondo' ? (
                                                <span className="text-xs font-bold px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full">TAEKWONDO</span>
                                            ) : (
                                                <span className="text-xs font-bold px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-full">CLIENT GYM</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de Réabonnement direct pour client scanné avec abonnement expiré */}
            <ReabonnementModal
                isOpen={isReaboOpen}
                mode="abonnement"
                initialClient={reaboClient}
                onClose={() => {
                    setIsReaboOpen(false);
                    setReaboClient(null);
                }}
                onSuccess={(receipt) => {
                    setReceiptData(receipt);
                    setIsReceiptOpen(true);
                    setIsReaboOpen(false);
                    setReaboClient(null);
                    setScanResult(null);
                    fetchAttendances();
                }}
            />

            {/* Reçu après réabonnement */}
            <ReceiptModal
                isOpen={isReceiptOpen}
                onClose={() => setIsReceiptOpen(false)}
                type="subscription"
                data={receiptData || {}}
            />
        </div>
    );
};

export default Pointage;
